'use client';

import * as React from "react";
import { Compass, LayoutDashboard, Wrench } from "lucide-react";
import { cn } from "../../utils/cn";

/**
 * ViewModePicker — Onboarding v2 layout×autonomy selector.
 *
 * The IDE UX spec (project_ide_ux_direction) defines three learner modes:
 *   guided   — Companion drives; minimal chrome. For new learners.
 *   standard — Balanced Companion + full IDE surface. Default.
 *   studio   — Everything visible; expert layout.
 *
 * Two layouts supported:
 *   layout="card" — 3 large cards side-by-side (onboarding).
 *   layout="row"  — Compact segmented control (in-app settings).
 */

export type ViewMode = "guided" | "standard" | "studio";

export interface ViewModePickerProps {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
  layout?: "card" | "row";
  className?: string;
  /** Override the default copy for each option. */
  labels?: Partial<Record<ViewMode, { title: string; description: string }>>;
}

const DEFAULT_LABELS: Record<ViewMode, { title: string; description: string }> = {
  guided: {
    title: "Guided",
    description: "Companion leads. Best for your first weeks.",
  },
  standard: {
    title: "Standard",
    description: "Balanced Companion + IDE. Recommended.",
  },
  studio: {
    title: "Studio",
    description: "Expert layout with every panel visible.",
  },
};

const iconFor: Record<ViewMode, React.ComponentType<{ className?: string }>> = {
  guided: Compass,
  standard: LayoutDashboard,
  studio: Wrench,
};

const modes: ViewMode[] = ["guided", "standard", "studio"];

export const ViewModePicker = React.forwardRef<HTMLDivElement, ViewModePickerProps>(
  function ViewModePicker(
    { value, onChange, layout = "card", labels, className },
    ref,
  ) {
    const merged = { ...DEFAULT_LABELS, ...(labels ?? {}) } as typeof DEFAULT_LABELS;
    if (layout === "row") {
      return (
        <div
          ref={ref}
          role="radiogroup"
          aria-label="View mode"
          data-slot="view-mode-picker"
          data-layout="row"
          className={cn(
            "inline-flex items-center gap-space-4 p-space-4",
            "bg-chrome-panel border border-chrome-border rounded-card",
            className,
          )}
        >
          {modes.map((m) => {
            const Icon = iconFor[m];
            const selected = value === m;
            return (
              <button
                key={m}
                role="radio"
                aria-checked={selected}
                onClick={() => onChange(m)}
                className={cn(
                  "inline-flex items-center gap-space-8 h-[32px] px-space-12 text-caption-12 font-medium rounded-chip",
                  "transition-colors duration-micro ease-standard",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-violet",
                  selected
                    ? "bg-chrome-surface text-ink-primary shadow-sm"
                    : "text-ink-muted hover:text-ink-primary",
                )}
              >
                <Icon className="size-4" />
                {merged[m].title}
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        role="radiogroup"
        aria-label="View mode"
        data-slot="view-mode-picker"
        data-layout="card"
        className={cn(
          "grid gap-space-16 sm:grid-cols-3",
          className,
        )}
      >
        {modes.map((m) => {
          const Icon = iconFor[m];
          const selected = value === m;
          return (
            <button
              key={m}
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(m)}
              className={cn(
                "group text-left flex flex-col gap-space-12 p-space-20",
                "bg-chrome-surface border rounded-card",
                "transition-colors duration-standard ease-standard",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-violet",
                selected
                  ? "border-accent-violet ring-2 ring-accent-violet/20"
                  : "border-chrome-border hover:border-ink-muted",
              )}
            >
              <span
                className={cn(
                  "inline-flex items-center justify-center size-10 rounded-card",
                  selected
                    ? "bg-accent-violet text-white"
                    : "bg-chrome-panel text-accent-violet",
                )}
              >
                <Icon className="size-5" />
              </span>
              <span className="text-heading-22 text-ink-primary">
                {merged[m].title}
              </span>
              <span className="text-body-14 text-ink-muted">
                {merged[m].description}
              </span>
            </button>
          );
        })}
      </div>
    );
  },
);

ViewModePicker.displayName = "ViewModePicker";
